import React, { useEffect, useMemo, useState } from 'react'
import { MailIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import {
  PERMISSION_CATALOG,
  assignableRolesForActor,
  getRoleDefaultPermissions,
} from '../../lib/permissions'
import type { LoginAccessFormState, ManagedUser } from '../../lib/adminApi'

const CATEGORY_ORDER = [
  'Core',
  'Sales',
  'Operations',
  'Customers & Properties',
  'Financial',
  'Communication',
  'System',
]

export function emptyLoginAccessForm(
  defaults?: Partial<LoginAccessFormState>
): LoginAccessFormState {
  const role = defaults?.role || 'crew_member'
  return {
    email: defaults?.email ?? '',
    first_name: defaults?.first_name ?? '',
    last_name: defaults?.last_name ?? '',
    phone: defaults?.phone ?? '',
    role,
    is_active: defaults?.is_active ?? true,
    customer_id: defaults?.customer_id ?? '',
    permissions: defaults?.permissions ?? getRoleDefaultPermissions(role),
    customized: defaults?.customized ?? false,
    password: defaults?.password ?? '',
    createMode: defaults?.createMode ?? 'invite',
  }
}

export function loginFormFromUser(u: ManagedUser): LoginAccessFormState {
  const role = u.role || 'crew_member'
  return emptyLoginAccessForm({
    email: u.email || '',
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    phone: u.phone || '',
    role,
    is_active: u.is_active !== false,
    customer_id: u.customer_id || '',
    permissions: u.effective_permissions || getRoleDefaultPermissions(role),
    customized: false,
    password: '',
    createMode: 'invite',
  })
}

interface LoginAccessPanelProps {
  value: LoginAccessFormState
  onChange: (next: LoginAccessFormState) => void
  /** Existing linked user (edit mode) */
  editingUser: ManagedUser | null
  actorRole: string
  /** Show email + createMode when creating a brand-new login */
  isNewLogin: boolean
  /** Hide name fields when Profile tab already owns them */
  hideNameFields?: boolean
  readOnly?: boolean
  onResendInvite?: () => void
  canResendInvite?: boolean
  onDeactivate?: () => void
  onDeleteLogin?: () => void
  actionBusy?: boolean
}

/**
 * Role, permissions, portal customer link, and optional new-login fields.
 * Controlled — parent owns save.
 */
export function LoginAccessPanel({
  value,
  onChange,
  editingUser,
  actorRole,
  isNewLogin,
  hideNameFields = false,
  readOnly = false,
  onResendInvite,
  canResendInvite = false,
  onDeactivate,
  onDeleteLogin,
  actionBusy = false,
}: LoginAccessPanelProps) {
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([])

  const assignableRoles = useMemo(
    () =>
      assignableRolesForActor(actorRole || 'crew_member').filter(
        (r) => r.value !== 'super_admin' && r.value !== 'platform_admin'
      ),
    [actorRole]
  )

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, typeof PERMISSION_CATALOG> = {}
    for (const p of PERMISSION_CATALOG) {
      if (!groups[p.category]) groups[p.category] = []
      groups[p.category].push(p)
    }
    return CATEGORY_ORDER.filter((c) => groups[c]).map((c) => ({ category: c, items: groups[c] }))
  }, [])

  useEffect(() => {
    ;(async () => {
      const { data } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .order('company_name')
        .limit(500)
      setCustomers(
        (data || []).map((c: any) => ({
          id: c.id,
          name:
            c.company_name ||
            `${c.contact_first_name || ''} ${c.contact_last_name || ''}`.trim() ||
            'Customer',
        }))
      )
    })()
  }, [])

  const patch = (partial: Partial<LoginAccessFormState>) => onChange({ ...value, ...partial })

  const applyRoleDefaults = (role: string) => {
    patch({
      role,
      permissions: getRoleDefaultPermissions(role),
      customized: false,
    })
  }

  const togglePermission = (key: string) => {
    const perms = new Set(value.permissions)
    if (perms.has(key)) perms.delete(key)
    else perms.add(key)
    patch({ permissions: Array.from(perms), customized: true })
  }

  const disabled = readOnly

  return (
    <div className="space-y-4">
      {isNewLogin && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={value.email}
              disabled={disabled}
              onChange={(e) => patch({ email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
          </div>
          <div className="flex flex-col gap-1.5 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="createMode"
                disabled={disabled}
                checked={value.createMode === 'invite'}
                onChange={() => patch({ createMode: 'invite' })}
              />
              Send invite email (they set password)
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="createMode"
                disabled={disabled}
                checked={value.createMode === 'create'}
                onChange={() => patch({ createMode: 'create' })}
              />
              Set password now
            </label>
          </div>
          {value.createMode === 'create' && (
            <input
              type="password"
              value={value.password}
              disabled={disabled}
              onChange={(e) => patch({ password: e.target.value })}
              placeholder="Password (min 8 characters)"
              autoComplete="new-password"
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
          )}
        </>
      )}

      {!isNewLogin && editingUser && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Login email</label>
            <input
              type="email"
              value={value.email}
              disabled={disabled}
              onChange={(e) => patch({ email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              {editingUser.invite_pending && (
                <span className="text-amber-600">Invite pending</span>
              )}
              {canResendInvite && editingUser.invite_pending && onResendInvite && (
                <button
                  type="button"
                  onClick={onResendInvite}
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  <MailIcon className="w-3 h-3" /> Resend invite
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              type="password"
              value={value.password}
              disabled={disabled}
              onChange={(e) => patch({ password: e.target.value })}
              placeholder="Leave blank to keep current password"
              autoComplete="new-password"
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">Optional. Minimum 8 characters if set.</p>
          </div>
        </div>
      )}

      {!hideNameFields && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
            <input
              value={value.first_name}
              disabled={disabled}
              onChange={(e) => patch({ first_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
            <input
              value={value.last_name}
              disabled={disabled}
              onChange={(e) => patch({ last_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            value={value.role}
            disabled={disabled}
            onChange={(e) => applyRoleDefaults(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
          >
            {assignableRoles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={disabled}
              checked={value.is_active}
              onChange={(e) => patch({ is_active: e.target.checked })}
            />
            Active account
          </label>
        </div>
      </div>

      {value.role === 'customer' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Linked customer (portal access)
          </label>
          <select
            value={value.customer_id}
            disabled={disabled}
            onChange={(e) => patch({ customer_id: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-50"
          >
            <option value="">Select customer…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Portal users only see data for this customer.</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Permissions</label>
          {value.customized && (
            <span className="text-xs text-amber-600">Customized from role defaults</span>
          )}
        </div>
        <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
          {groupedPermissions.map(({ category, items }) => (
            <div key={category} className="p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{category}</p>
              <div className="space-y-1">
                {items.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={value.permissions.includes(p.key)}
                      onChange={() => togglePermission(p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isNewLogin && editingUser && !readOnly && (onDeactivate || onDeleteLogin) && (
        <div className="pt-4 border-t border-gray-200 space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account actions</p>
          <div className="flex flex-wrap gap-2">
            {onDeactivate && (
              <button
                type="button"
                disabled={actionBusy || value.is_active === false}
                onClick={onDeactivate}
                className="px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
              >
                Deactivate account
              </button>
            )}
            {onDeleteLogin && (
              <button
                type="button"
                disabled={actionBusy}
                onClick={onDeleteLogin}
                className="px-3 py-2 text-sm rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
              >
                Delete login
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Deactivate keeps the account and email. Delete permanently removes the login so the email can be reused.
          </p>
        </div>
      )}
    </div>
  )
}